import React from "react";
import propTypes from "prop-types";
import classnames from "classnames";
import Collapsible from "react-collapsible";
import Icon from "../icons/Icon";
import { guid, createDataAttributes } from "../../utilities/index";

import "./Panel.scss";

const Panel = ({
  id = guid(),
  title,
  className,
  collapsible,
  children,
  isOpen,
  highlight,
  bgcolor,
  titlebar,
  shadow,
  rounded,
  noBorder,
  footer,
  dataAttrib,
  dataTitleAttrib,
  ...rest
}) => {
  const classes = classnames(
    "uikit__panel",
    className,
    "uikit__panel__highlight-" + highlight,
    {
      "uikit__panel__bg-grey": bgcolor === "grey",
      uikit__panel__titlebar: titlebar,
      uikit__panel__shadowed: shadow,
      uikit__panel__rounded: rounded,
      uikit__panel__noBorder: noBorder,
      "uikit__panel__has-footer": !!footer
    }
  );

  const titleEl = title ? (
    <div
      className="uikit__panel__title"
      {...createDataAttributes(dataTitleAttrib)}
    >
      <div className="uikit__panel__title-focus-highlight"></div>
      <div id={`panel-title-${id}`} className="uikit__panel__title-text">
        {title}
      </div>
      {collapsible && (
        <>
          <span className="uikit__panel__chevron">
            <Icon icon="fasChevronCircleDown" />
          </span>
          <button
            aria-labelledby={`panel-title-${id}`}
            className="uikit-sr-only"
          />
        </>
      )}
    </div>
  ) : null;

  const closedClass = classnames("uikit__panel__is-closed");
  const openedClass = classnames("uikit__panel__is-open");

  if (collapsible) {
    return (
      <div className={classes} {...createDataAttributes(dataAttrib)} {...rest}>
        <Collapsible
          contentElementId={id && `collapsible-content-${id}`}
          triggerElementProps={id && { id: `collapsible-trigger-${id}` }}
          className={closedClass}
          openedClassName={openedClass}
          open={isOpen}
          trigger={titleEl}
          overflowWhenOpen="visible"
          triggerDisabled={!collapsible}
          contentInnerClassName="uikit__panel__content"
        >
          <div className="uikit__panel__body">{children}</div>
          {footer && <div className="uikit__panel__footer">{footer}</div>}
        </Collapsible>
      </div>
    );
  }

  // if it's not collapsible, then render out the panel without using the Collapsible component
  return (
    <div className={classes} {...createDataAttributes(dataAttrib)} {...rest}>
      {titleEl}
      <div className="uikit__panel__content">
        <div className="uikit__panel__body">{children}</div>
        {footer && <div className="uikit__panel__footer">{footer}</div>}
      </div>
    </div>
  );
};

Panel.propTypes = {
  id: propTypes.string,
  title: propTypes.oneOfType([
    propTypes.arrayOf(propTypes.node),
    propTypes.node,
    propTypes.element,
    propTypes.string
  ]),
  className: propTypes.string,
  collapsible(props) {
    if (props.collapsible && !props.title) {
      return new Error('"title" is required when using a collapsible panel');
    }
  },
  isOpen: propTypes.bool,
  children: propTypes.oneOfType([
    propTypes.arrayOf(propTypes.node),
    propTypes.node,
    propTypes.element
  ]).isRequired,
  highlight: propTypes.oneOf(["green", "blue", "red", "grey", "none"]),
  bgcolor: propTypes.oneOf(["white", "grey"]),
  titlebar: propTypes.bool,
  shadow: propTypes.bool,
  rounded: propTypes.bool,
  noBorder: propTypes.bool,
  footer: propTypes.oneOfType([
    propTypes.node,
    propTypes.element,
    propTypes.func
  ]),
  dataAttrib: propTypes.arrayOf(Object),
  dataTitleAttrib: propTypes.arrayOf(Object)
};

Panel.defaultProps = {
  isOpen: true,
  highlight: "none",
  shadow: false,
  noBorder: false
};

export default Panel;
